import { translate } from "@ollie-sports/i18n";
import { PrettyCoolTextInputWithLabel, PrettyCoolTextAreaWithLabel, Form, FormRef } from "../../../components/Form";
import { CoolCheckboxInput } from "../../../components/Inputs/CoolCheckboxInput";
import { LINK_REGEX, EMAIL_REGEX } from "../../../utils/Regexes";
import { OrgSettings } from "@ollie-sports/models";
import { useImmutableState } from "../../../utils/useImmutableState";
import { useRef } from "react";
import { Button } from "@material-ui/core";
import { common__magicDeleteValue } from "@ollie-sports/core/dist/api";
import { getBifrost } from "../../../services/bifrost.service";
import { openErrorToast } from "../../../utils/openErrorToast";
import { InfoTooltipIcon } from "../../../components/InfoTooltip";
import { StyledButton } from "../../../components/StyledButton";
import { Prompt } from "react-router";
import { dequal } from "dequal";
import { useOrgSettings } from "../../../hooks/useOrgSettings";

export function OrgTryoutSettings(p: { orgId: string; initialOrgSettings: OrgSettings }) {
  const [orgSettings, setOrgSettings] = useImmutableState<OrgSettings>(p.initialOrgSettings);
  const { orgSettings: subscribedOrgSettings } = useOrgSettings({ orgId: p.orgId });
  const formRef = useRef<FormRef>(null);

  return (
    <Form ref={formRef}>
      {() => (
        <>
          <Prompt
            message={translate({ defaultMessage: "Are you sure you wish to navigate? You have unsaved changes." })}
            when={!dequal(subscribedOrgSettings, orgSettings)}
          />
          <PrettyCoolTextInputWithLabel
            label={translate({ defaultMessage: "Default Tryout Reply Email" })}
            onChange={newVal => {
              setOrgSettings({
                tryoutSettings: { ...orgSettings.tryoutSettings, defaultReplyToEmailAddress: newVal?.toLowerCase() ?? "" }
              });
            }}
            infoTooltip={translate({
              defaultMessage:
                'The default "reply to" email address members see when receiving emails regarding tryouts, such as their tryout registration receipt.'
            })}
            validate={v => {
              if (v) {
                if (!v.match(LINK_REGEX)) {
                  return translate({ defaultMessage: "Must be a valid email" });
                }
              }
              return "";
            }}
            inputProps={{ className: "sm:max-w-xs", placeholder: translate({ defaultMessage: "Enter Email Address" }) }}
            value={orgSettings.tryoutSettings?.defaultReplyToEmailAddress ?? ""}
          />

          <CoolCheckboxInput
            style={{ marginTop: 16 }}
            label={translate({ defaultMessage: "Restrict Tryout Visibility" })}
            infoTooltip={translate({
              defaultMessage:
                "When restricted, coaches and other team admins will only be able to view tryout registrations that exactly match the teams they manage."
            })}
            value={orgSettings.tryoutSettings?.enableRestrictedTryoutMode ?? false}
            labelType="inside"
            onChange={newVal => {
              setOrgSettings({
                tryoutSettings: {
                  ...orgSettings.tryoutSettings,
                  enableRestrictedTryoutMode: !orgSettings.tryoutSettings?.enableRestrictedTryoutMode
                }
              });
            }}
          />

          <PrettyCoolTextInputWithLabel
            label={translate({ defaultMessage: "Tryout Offer Expiration Hours" })}
            style={{ marginTop: 16 }}
            infoTooltip={translate({
              defaultMessage:
                "Number of hours until tryout offers will expire. Coaches can override the expiration time when sending individual offers to players."
            })}
            onChange={newVal => {
              const newValWithoutMinusSign = newVal?.replaceAll("-", "");
              if (!newValWithoutMinusSign) {
                setOrgSettings({
                  tryoutSettings: { ...orgSettings.tryoutSettings, defaultOfferExpirationTimeInHrs: undefined }
                });
                return;
              }
              try {
                const newValInt = parseInt(newValWithoutMinusSign);
                if (newValInt) {
                  setOrgSettings({
                    tryoutSettings: { ...orgSettings.tryoutSettings, defaultOfferExpirationTimeInHrs: newValInt }
                  });
                }
              } catch (e) {}
            }}
            inputProps={{
              className: "sm:max-w-xs",
              placeholder: translate({ defaultMessage: "Enter # of Hours" }),
              type: "number"
            }}
            value={`${orgSettings.tryoutSettings?.defaultOfferExpirationTimeInHrs ?? ""}`}
          />
          <PrettyCoolTextAreaWithLabel
            label={translate({ defaultMessage: "Tryout Signup Confirmation Email" })}
            style={{ marginTop: 16 }}
            onChange={newVal => {
              setOrgSettings({ tryoutSettings: { ...orgSettings.tryoutSettings, confirmationEmailMessage: newVal } });
            }}
            infoTooltip={translate({
              defaultMessage:
                "The email sent to parents/guardians immediately after they successfully sign up for a tryout session."
            })}
            textAreaProps={{
              placeholder: translate({
                defaultMessage: "Type email message..."
              })
            }}
            value={`${orgSettings.tryoutSettings?.confirmationEmailMessage ?? ""}`}
          />
          <PrettyCoolTextAreaWithLabel
            label={translate({ defaultMessage: "Default Tryout Offer Email" })}
            style={{ marginTop: 16 }}
            infoTooltip={translate({
              defaultMessage:
                "The default email template sent to players receiving an offer. Coaches will be able to customize the email before sending."
            })}
            onChange={newVal => {
              setOrgSettings({ tryoutSettings: { ...orgSettings.tryoutSettings, defaultOfferEmailText: newVal } });
            }}
            textAreaProps={{
              placeholder: translate({
                defaultMessage: "Type email message..."
              })
            }}
            value={`${orgSettings.tryoutSettings?.defaultOfferEmailText ?? ""}`}
          />
          <StyledButton
            className="mt-4"
            text={translate.common.Save}
            onClick={async () => {
              try {
                await getBifrost().org__server__updateOrgSettings.fetchServer({
                  id: p.initialOrgSettings.id,
                  orgSettings: {
                    tryoutSettings: {
                      confirmationEmailMessage:
                        orgSettings.tryoutSettings?.confirmationEmailMessage ?? common__magicDeleteValue(),
                      defaultOfferEmailText: orgSettings.tryoutSettings?.defaultOfferEmailText ?? common__magicDeleteValue(),
                      defaultOfferExpirationTimeInHrs:
                        orgSettings.tryoutSettings?.defaultOfferExpirationTimeInHrs ?? common__magicDeleteValue(),
                      defaultReplyToEmailAddress:
                        orgSettings.tryoutSettings?.defaultReplyToEmailAddress ?? common__magicDeleteValue(),
                      enableRestrictedTryoutMode:
                        orgSettings.tryoutSettings?.enableRestrictedTryoutMode ?? common__magicDeleteValue()
                    }
                  }
                });
              } catch (e) {
                openErrorToast(
                  translate({
                    defaultMessage:
                      "There was a problem updating the settings. Please try again or contact support@olliesports.com"
                  })
                );
              }
            }}
          />
        </>
      )}
    </Form>
  );
}
